import * as React from 'react';
import { styled } from '@linaria/react';

import useInterval from '@/hooks/use-interval';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import { useColorMode } from '@/components/UserPreferencesProvider';
import { LightWrapper } from '@/components/ColorModeContainer';
import Demo from '@/components/Demo';
import SliderControl from '@/components/Controls/SliderControl';

const NUM_FRAMES = 29;

function SpriteConceptDemo() {
  const [frameDuration, setFrameDuration] = React.useState(500);
  const [frameIndex, setFrameIndex] = React.useState(0);

  const colorMode = useColorMode();

  const prefersReducedMotion = usePrefersReducedMotion();

  useInterval(
    () => {
      setFrameIndex((prevIndex) => (prevIndex + 1) % NUM_FRAMES);
    },
    frameDuration === 1000 ? null : frameDuration
  );

  const backgroundPosition = React.useMemo(() => {
    const frameWidth = 100;
    const offset = -(frameIndex * frameWidth);
    return `translateX(${offset}px)`;
  }, [frameIndex]);

  const foregroundPosition = React.useMemo(() => {
    const percentage = (frameIndex / (NUM_FRAMES - 1)) * 100;
    return `${percentage}% 0%`;
  }, [frameIndex]);

  const includeBriefTransition =
    !prefersReducedMotion && frameDuration >= 350;

  return (
    <Demo
      id="sprite-concept-demo"
      outputWrapper={OutputWrapper}
      outputWrapperProps={{
        as: colorMode === 'light' ? LightWrapper : undefined,
      }}
      initialValues={{}}
      renderAltControls={() => (
        <ControlsWrapper>
          <SliderControl
            id="frameDuration"
            label="Show each frame for"
            value={frameDuration}
            setValue={setFrameDuration}
            handleReset={() => setFrameDuration(500)}
            min={10}
            max={1000}
            step={5}
            previewRenderer={(value) =>
              value === 1000 ? '∞ (Paused)' : `${value}ms`
            }
          />
        </ControlsWrapper>
      )}
    >
      {() => (
        <SpriteViewport>
          <ViewportFrame>
            <BackgroundSprite
              src="/images/sprites/twitter-like-sprite.webp"
              alt=""
              draggable={false}
              style={{
                transform: backgroundPosition,
                transitionDuration: includeBriefTransition
                  ? `${frameDuration * 0.25}ms`
                  : '0ms',
              }}
            />
            <ForegroundWrapper>
              <ForegroundSprite
                src="/images/sprites/twitter-like-sprite.webp"
                alt="Current frame of sprite animation"
                draggable={false}
                style={{
                  objectPosition: foregroundPosition,
                  transitionDelay: includeBriefTransition
                    ? `${frameDuration * 0.05}ms`
                    : '0ms',
                }}
              />
            </ForegroundWrapper>
          </ViewportFrame>

          <GradientFade />
        </SpriteViewport>
      )}
    </Demo>
  );
}

const OutputWrapper = styled.div`
  overflow: hidden;
  padding-block: 24px;
`;

const ControlsWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  margin-inline: auto;
`;

const SpriteViewport = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 120px;
`;

const ViewportFrame = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
`;

const BackgroundSprite = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100px * ${NUM_FRAMES});
  max-width: revert;
  height: 100px;
  object-fit: none;
  opacity: 0.325;
  user-select: none;
  transition: transform 50ms;
`;

const ForegroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  background: var(--color-background);
  outline: 2px solid var(--color-gray-800);
  border-radius: 2px;
  outline-offset: 2px;
  transition: object-position 0ms;
`;

const ForegroundSprite = styled.img`
  position: absolute;
  inset: 0;
  width: 100px;
  height: 100px;
  object-fit: cover;
  object-position: 0% 0%;
  user-select: none;
  outline: 2px solid var(--color-gray-300);
  border-radius: 2px;
`;

const GradientFade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    var(--color-background),
    transparent 20% 80%,
    var(--color-background)
  );
`;

export default SpriteConceptDemo;
